import { motion, useReducedMotion } from '@/lib/motion';
import { Bot, Calendar, CheckCircle2, Clock, Headphones, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Waveform } from '@/components/marketing/waveform';

const HERO_TURNS = [
  { role: 'caller' as const, text: 'Hi, I’d like to book a haircut for tomorrow afternoon.' },
  {
    role: 'agent' as const,
    text: 'Of course! We take bookings from 2 PM tomorrow. Does mid-afternoon work for you?',
  },
  { role: 'caller' as const, text: 'Around 3 sounds good.' },
  {
    role: 'agent' as const,
    text: 'Noted — around 3 PM tomorrow. Can I grab your name and number so the team can confirm your spot?',
  },
];

const HERO_LOOP_HOLD_MS = 3200;
const HERO_TURN_MS = 1600;

/**
 * Looping mini phone-call: turns appear one by one, an outcome chip lands, the
 * loop holds, then restarts. Static (all turns visible) under
 * prefers-reduced-motion. Shared by the home dual-channel hero and the /voice
 * product page.
 */
export function HeroCallCard(): JSX.Element {
  const reduced = useReducedMotion();
  const total = HERO_TURNS.length;
  const [visible, setVisible] = useState(reduced ? total + 1 : 1);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (reduced) return;
    const timer = setTimeout(
      () => {
        setVisible((v) => {
          if (v > total) {
            setElapsed(0); // restart the call-timer chrome with the loop
            return 1;
          }
          return v + 1;
        });
      },
      visible > total ? HERO_LOOP_HOLD_MS : HERO_TURN_MS,
    );
    return () => clearTimeout(timer);
  }, [reduced, visible, total]);

  // Call-timer chrome: ticks once a second while the card is animating.
  useEffect(() => {
    if (reduced) return;
    const tick = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(tick);
  }, [reduced]);

  const clock = `00:${String(34 + (elapsed % 26)).padStart(2, '0')}`;

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card/60 shadow-2xl shadow-[var(--brand-accent)]/10 backdrop-blur">
      {/* Window chrome */}
      <div className="flex items-center justify-between border-b border-border bg-muted/30 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-red-500/70" />
          <span className="h-3 w-3 rounded-full bg-amber-500/70" />
          <span className="h-3 w-3 rounded-full bg-emerald-500/70" />
        </div>
        <div className="flex items-center gap-2 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-400">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75 motion-reduce:animate-none" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          Live call · {clock}
        </div>
        <Waveform bars={12} className="h-4 w-14" />
      </div>

      {/* Transcript — every turn AND the outcome chip stay mounted at all times
          so the card's height is constant across the whole loop. Reveal is
          opacity/translate, not node add/remove; this is what stops the hero
          from reflowing (and the whole centered row from shifting) each turn. */}
      <div className="space-y-4 px-6 py-6">
        {HERO_TURNS.map((turn, i) => {
          const shown = i < visible;
          return (
            <motion.div
              key={i}
              initial={false}
              animate={{ opacity: shown ? 1 : 0, y: shown ? 0 : 10 }}
              transition={{ duration: 0.25 }}
              aria-hidden={!shown}
            >
              <CallBubble role={turn.role} typing={!reduced && i === visible - 1 && visible < total}>
                {turn.text}
              </CallBubble>
            </motion.div>
          );
        })}
        <motion.div
          initial={false}
          animate={{ opacity: visible > total ? 1 : 0, y: visible > total ? 0 : 8 }}
          transition={{ duration: 0.25 }}
          className="flex justify-center"
          aria-hidden={visible <= total}
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-4 py-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="h-4 w-4" />
            Booking request captured · summary sent to dashboard
          </span>
        </motion.div>
      </div>

      {/* Footer stats */}
      <div className="grid grid-cols-3 divide-x divide-border border-t border-border bg-muted/20 text-center text-xs">
        <CardStat icon={<Clock className="h-3.5 w-3.5" />} label="Avg response" value="380ms" />
        <CardStat icon={<Calendar className="h-3.5 w-3.5" />} label="Availability" value="24/7" />
        <CardStat icon={<Zap className="h-3.5 w-3.5" />} label="Setup" value="< 10 min" />
      </div>
    </div>
  );
}

function CallBubble({
  role,
  children,
  typing,
}: {
  role: 'agent' | 'caller';
  children: React.ReactNode;
  typing?: boolean;
}): JSX.Element {
  const isAgent = role === 'agent';
  return (
    <div className={`flex gap-3 ${isAgent ? 'flex-row' : 'flex-row-reverse'}`}>
      <div
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
          isAgent
            ? 'bg-[var(--brand-accent)]/15 text-[var(--brand-accent)]'
            : 'bg-muted text-muted-foreground'
        }`}
      >
        {isAgent ? <Bot className="h-4 w-4" /> : <Headphones className="h-4 w-4" />}
      </div>
      <div
        className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
          isAgent
            ? 'rounded-tl-sm bg-muted/50 text-foreground'
            : 'rounded-tr-sm bg-[var(--brand-accent)] text-white'
        }`}
      >
        {children}
        {typing ? (
          <span className="ml-2 inline-flex gap-1 align-middle">
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current opacity-60 [animation-delay:-0.3s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current opacity-60 [animation-delay:-0.15s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-current opacity-60" />
          </span>
        ) : null}
      </div>
    </div>
  );
}

function CardStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}): JSX.Element {
  return (
    <div className="px-3 py-3">
      <div className="flex items-center justify-center gap-1.5 text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-semibold text-foreground">{value}</div>
    </div>
  );
}
